import { useMutation } from '@tanstack/react-query';

import { PREFIX_URL } from '@/src/api/config';
import { axios } from '@/src/lib/axios/axios';
import { MutationConfig, queryClient } from '@/src/lib/react-query/react-query';
import { generateRoutePath } from '@/src/routes';

import { UpdateEmailEntryResponse } from './config';
import { acivityLogsKeys } from './getActivityLogs';
import { emailEntriesKeys } from './getEmailEntries';
import { emailEntryKeys } from './getEmailEntry';
import { emailThreadAttachmentsKeys } from './getEmailThreadAttachments';

import { isResponseError } from '../../../../api/config';
import { entryActionCustomValidateOptions } from '../../../../api/inbox';
import { EmailDeliveryType } from '../../../../types';

// const url = `${PREFIX_URL}/v01/inbox/isMisc`;
const url = `${PREFIX_URL}/inbox-threads/:inboxEntryId/emails/:emailId`;

export type MarkMiscellaneousDTO = {
  inboxEntryId: number;
  emailId: number;
  misc: boolean;
  projectId: string;
};

export const markMiscellaneous = ({
  projectId,
  inboxEntryId,
  emailId,
  misc,
}: MarkMiscellaneousDTO): Promise<UpdateEmailEntryResponse> => {
  return axios.put(
    generateRoutePath(url, { projectId, inboxEntryId, emailId }),
    {
      misc,
    },
    {
      ...entryActionCustomValidateOptions,
    }
  );
};

type useMarkMiscellaneousOptions = {
  projectId: string;
  inboxEntryId: number;
  emailId: number;
  config?: MutationConfig<typeof markMiscellaneous>;
};

export const useMarkMiscellaneous = ({
  projectId,
  inboxEntryId,
  emailId,
  config,
}: useMarkMiscellaneousOptions) => {
  return useMutation({
    onSuccess: (data) => {
      if (isResponseError(data)) {
        return;
      }

      queryClient.invalidateQueries([
        ...acivityLogsKeys.detail({ projectId, emailId, inboxEntryId }),
      ]);

      queryClient.setQueriesData(
        [...emailEntriesKeys.lists({ projectId, inboxEntryId })],
        (prev: any) => {
          return {
            ...prev,
            pages: prev.pages.map((page) => {
              return {
                ...page,
                data: {
                  ...page.data,
                  emails: page.data.emails.map((emailEntry) => {
                    if (emailEntry.id === emailId) {
                      return {
                        ...emailEntry,
                        ...data.data['inbox-email'],
                      };
                    }
                    return emailEntry;
                  }),
                },
              };
            }),
          };
        }
      );

      queryClient.setQueryData(
        [...emailEntryKeys.detail({ emailId, inboxEntryId, projectId })],
        (prev: any) => {
          return {
            ...prev,
            data: data.data['inbox-email'],
          };
        }
      );

      const updatedFileIds = {};

      for (const file of data.data['inbox-email'].attachments) {
        updatedFileIds[file.id] = file;
      }

      if (Object.keys(updatedFileIds).length > 0) {
        queryClient.setQueriesData(
          [
            ...emailThreadAttachmentsKeys.lists({
              inboxEntryId,
              projectId,
              type: EmailDeliveryType.inbound,
            }),
          ],
          (prev: any) => {
            return {
              ...prev,
              pages: prev.pages.map((page) => {
                return {
                  ...page,
                  data: {
                    ...page.data,
                    attachments: page.data.attachments.map((attachmentItem) => {
                      const updatedFile = updatedFileIds[attachmentItem.id];

                      if (updatedFile) {
                        return {
                          ...attachmentItem,
                          status: updatedFile.status,
                          read: updatedFile.read,
                          'allowed-status-changes': updatedFile['allowed-status-changes'],
                          'change-status': updatedFile['change-status'],
                        };
                      }
                      return attachmentItem;
                    }),
                  },
                };
              }),
            };
          }
        );

        queryClient.setQueriesData(
          [
            ...emailThreadAttachmentsKeys.lists({
              inboxEntryId,
              projectId,
              type: EmailDeliveryType.outbound,
            }),
          ],
          (prev: any) => {
            return {
              ...prev,
              pages: prev.pages.map((page) => {
                return {
                  ...page,
                  data: {
                    ...page.data,
                    attachments: page.data.attachments.map((attachmentItem) => {
                      const updatedFile = updatedFileIds[attachmentItem.id];

                      if (updatedFile) {
                        return {
                          ...attachmentItem,
                          status: updatedFile.status,
                          read: updatedFile.read,
                          'allowed-status-changes': updatedFile['allowed-status-changes'],
                          'change-status': updatedFile['change-status'],
                        };
                      }
                      return attachmentItem;
                    }),
                  },
                };
              }),
            };
          }
        );
      }
    },
    ...config,
    mutationFn: markMiscellaneous,
  });
};
